import {
  GlobalStateRecord,
  useGlobalStateBatch,
  useOrganisationList,
} from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import {
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import { Col, Form, Row } from "react-bootstrap";
import CollapsibleCard from "../CollapsibleCard";

const SiteInvoiceProps = ({
  site,
  setSite,
}: {
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
}) => {
  const { globalStateForType } = useGlobalStateBatch();
  const { fetchOrganisations } = useOrganisationList();

  const orgsById = (fetchOrganisations.data?.organisations || []).reduce(
    (acc: Record<string, Organisation>, org: Organisation) => {
      acc[org.id] = org;
      return acc;
    },
    {}
  );

  const isOwnerOccupied = (site.siteOwnerships || []).some(
    (so) => so.ownership === "ownerOccupier"
  );

  const tenantOwnership = (site.siteOwnerships || []).find(
    (so) => so.ownership === "tenant" || so.ownership === "ownerOccupier"
  );
  const landlordOwnership = (site.siteOwnerships || []).find(
    (so) => so.ownership === "landlord" || so.ownership === "ownerOccupier"
  );

  const tenantOrgInvoiceProps = tenantOwnership
    ? orgsById[tenantOwnership.orgID]?.invoiceProps
    : undefined;
  const landlordOrgInvoiceProps = landlordOwnership
    ? orgsById[landlordOwnership.orgID]?.invoiceProps
    : undefined;

  // Match backend: prefer tenant org props, fall back to landlord org props
  const orgPropsForGlobals = tenantOrgInvoiceProps || landlordOrgInvoiceProps;

  const bankAccountOptions: GlobalStateRecord[] = globalStateForType("bank_account");
  const tcsOptions: GlobalStateRecord[] = globalStateForType("terms_and_conditions");
  const footerOptions: GlobalStateRecord[] = globalStateForType("invoice_footer_text");
  const vatNoticeOptions: GlobalStateRecord[] = globalStateForType("vat_notice");
  const invoiceQuestionsOptions: GlobalStateRecord[] = globalStateForType("invoice_questions");

  const orgBankAccountName = bankAccountOptions.find(
    (opt) => opt.id === orgPropsForGlobals?.bankAccountId
  )?.name;
  const orgTcsName = tcsOptions.find(
    (opt) => opt.id === orgPropsForGlobals?.termsAndConditionsId
  )?.name;
  const orgFooterName = footerOptions.find(
    (opt) => opt.id === orgPropsForGlobals?.invoiceFooterTextId
  )?.name;
  const orgVatNoticeName = vatNoticeOptions.find(
    (opt) => opt.id === orgPropsForGlobals?.vatNoticeId
  )?.name;
  const orgInvoiceQuestionsName = invoiceQuestionsOptions.find(
    (opt) => opt.id === orgPropsForGlobals?.invoiceQuestionsId
  )?.name;

  return (
    <CollapsibleCard
      title="Invoice properties"
      className="mt-2"
      initialOpen={false}
    >
      {isOwnerOccupied ? (
        <Row className="mb-3">
          <Col sm={6}>
            <Form.Group className="mb-3">
              <Form.Label>Legal entity name</Form.Label>
              <Form.Control
                type="text"
                value={site.invoiceProps?.tenantLegalEntityName || ""}
                onChange={(e) =>
                  setSite({
                    invoiceProps: {
                      tenantLegalEntityName: e.target.value || null,
                    },
                  })
                }
                placeholder={
                  tenantOrgInvoiceProps?.legalEntityName || "No org default set"
                }
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3">
              <Form.Label>Entity address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={site.invoiceProps?.tenantEntityAddress || ""}
                onChange={(e) =>
                  setSite({
                    invoiceProps: {
                      tenantEntityAddress: e.target.value || null,
                    },
                  })
                }
                placeholder={
                  tenantOrgInvoiceProps?.entityAddress || "No org default set"
                }
              />
            </Form.Group>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tenant legal entity name</Form.Label>
                <Form.Control
                  type="text"
                  value={site.invoiceProps?.tenantLegalEntityName || ""}
                  onChange={(e) =>
                    setSite({
                    invoiceProps: {
                      tenantLegalEntityName: e.target.value || null,
                    },
                  })
                  }
                  placeholder={
                    tenantOrgInvoiceProps?.legalEntityName ||
                    "No org default set"
                  }
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tenant entity address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={site.invoiceProps?.tenantEntityAddress || ""}
                  onChange={(e) =>
                    setSite({
                    invoiceProps: {
                      tenantEntityAddress: e.target.value || null,
                    },
                  })
                  }
                  placeholder={
                    tenantOrgInvoiceProps?.entityAddress || "No org default set"
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label>Landlord legal entity name</Form.Label>
                <Form.Control
                  type="text"
                  value={site.invoiceProps?.landlordLegalEntityName || ""}
                  onChange={(e) =>
                    setSite({
                      invoiceProps: {
                        landlordLegalEntityName: e.target.value || null,
                      },
                    })
                  }
                  placeholder={
                    landlordOrgInvoiceProps?.legalEntityName ||
                    "No org default set"
                  }
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label>Landlord entity address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={site.invoiceProps?.landlordEntityAddress || ""}
                  onChange={(e) =>
                    setSite({
                      invoiceProps: {
                        landlordEntityAddress: e.target.value || null,
                      },
                    })
                  }
                  placeholder={
                    landlordOrgInvoiceProps?.entityAddress ||
                    "No org default set"
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      <Row className="mb-3">
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Bank account override</Form.Label>
            <Form.Select
              value={site.invoiceProps?.bankAccountId || ""}
              onChange={(e) =>
                setSite({
                  invoiceProps: { bankAccountId: e.target.value || null },
                })
              }
            >
              <option value="">
                {orgBankAccountName
                  ? `Inherit (${orgBankAccountName})`
                  : "Inherit from org"}
              </option>
              {bankAccountOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Terms & conditions override</Form.Label>
            <Form.Select
              value={site.invoiceProps?.termsAndConditionsId || ""}
              onChange={(e) =>
                setSite({
                  invoiceProps: {
                    termsAndConditionsId: e.target.value || null,
                  },
                })
              }
            >
              <option value="">
                {orgTcsName ? `Inherit (${orgTcsName})` : "Inherit from org"}
              </option>
              {tcsOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Invoice footer text override</Form.Label>
            <Form.Select
              value={site.invoiceProps?.invoiceFooterTextId || ""}
              onChange={(e) =>
                setSite({
                  invoiceProps: {
                    invoiceFooterTextId: e.target.value || null,
                  },
                })
              }
            >
              <option value="">
                {orgFooterName
                  ? `Inherit (${orgFooterName})`
                  : "Inherit from org"}
              </option>
              {footerOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>VAT notice override</Form.Label>
            <Form.Select
              value={site.invoiceProps?.vatNoticeId || ""}
              onChange={(e) =>
                setSite({
                  invoiceProps: { vatNoticeId: e.target.value || null },
                })
              }
            >
              <option value="">
                {orgVatNoticeName
                  ? `Inherit (${orgVatNoticeName})`
                  : "Inherit from org"}
              </option>
              {vatNoticeOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Invoice questions override</Form.Label>
            <Form.Select
              value={site.invoiceProps?.invoiceQuestionsId || ""}
              onChange={(e) =>
                setSite({
                  invoiceProps: {
                    invoiceQuestionsId: e.target.value || null,
                  },
                })
              }
            >
              <option value="">
                {orgInvoiceQuestionsName
                  ? `Inherit (${orgInvoiceQuestionsName})`
                  : "Inherit from org"}
              </option>
              {invoiceQuestionsOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </CollapsibleCard>
  );
};

export default SiteInvoiceProps;
